import React, { useCallback, useEffect, useState } from 'react';
import { authRef, firebaseRef } from '../reference';
import LiveKitRoomAudio from './LiveKitRoomAudio';
import LiveKitRoomLogs from './LiveKitRoomLogs';
import LiveKitRoomTranscript from './LiveKitRoomTranscript';
import LiveKitOverview from './LiveKitOverview';
import { displayDate, fullName, isAccessError, loadMonitoring, runningTime } from './monitoring-api';
import './LiveKitMonitoring.css';

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  async function signIn(event) {
    event.preventDefault(); setError(''); setLoading(true);
    try { await authRef.signInWithEmailAndPassword(email, password); setPassword(''); }
    catch (failure) { setError(failure.message); }
    finally { setLoading(false); }
  }
  async function google() {
    setError(''); setLoading(true);
    try { await authRef.signInWithPopup(new firebaseRef.GoogleAuthProvider()); }
    catch (failure) { setError(failure.code === 'auth/popup-blocked' ? 'Allow popups in this browser to sign in with Google.' : failure.message); }
    finally { setLoading(false); }
  }
  return <section className="monitor-login"><h2>Sign in to monitoring</h2><p>Use an account with monitoring access.</p>
    <button className="monitor-google" onClick={google} disabled={loading}>Sign in with Google</button>
    <p className="monitor-signin-divider">or use email and password</p>
    <form onSubmit={signIn}>
      <label htmlFor="monitor-email">Email</label><input id="monitor-email" autoComplete="username" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
      <label htmlFor="monitor-password">Password</label><input id="monitor-password" autoComplete="current-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} required />
      <button disabled={loading} type="submit">Sign in</button>
    </form>{error && <p role="alert" className="monitor-error">{error}</p>}
  </section>;
}

function Rooms({ user }) {
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState('');
  const [accessError, setAccessError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('audio');
  const [now, setNow] = useState(Date.now());
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const normalizedSearch = search.trim().toLowerCase();
  const onAccessError = useCallback((failure) => {
    setSelected(null); setSnapshot(null); setError('');
    setAccessError(failure.message || 'Monitoring access is no longer available.');
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(normalizedSearch), 500);
    return () => clearTimeout(timer);
  }, [normalizedSearch]);

  useEffect(() => {
    if (accessError || normalizedSearch !== query) return;
    const controller = new AbortController();
    let timer;
    let pending = false;
    async function update() {
      if (pending || controller.signal.aborted) return;
      clearTimeout(timer);
      if (document.visibilityState === 'hidden') return;
      pending = true;
      setSearchLoading(true);
      try {
        const next = await loadMonitoring(query ? `rooms?q=${encodeURIComponent(query)}` : 'rooms', user, controller.signal);
        if (controller.signal.aborted) return;
        if (!Array.isArray(next.rooms)) throw new Error('Monitoring returned an invalid room list. Retrying…');
        setSnapshot({ ...next, query }); setError('');
      } catch (failure) {
        if (controller.signal.aborted) return;
        if (isAccessError(failure)) { onAccessError(failure); return; }
        setError(failure.message);
      } finally {
        pending = false;
        if (!controller.signal.aborted) { setSearchLoading(false); timer = setTimeout(update, 3000); }
      }
    }
    const visible = () => { if (document.visibilityState === 'visible') void update(); };
    void update();
    document.addEventListener('visibilitychange', visible);
    return () => { controller.abort(); clearTimeout(timer); document.removeEventListener('visibilitychange', visible); };
  }, [user, refresh, accessError, onAccessError, query, normalizedSearch]);

  const currentRoom = selected && (snapshot?.rooms.find((room) => room.id === selected.id) || selected);
  const ended = selected && snapshot && !snapshot.query && !snapshot.truncated && !snapshot.rooms.some((room) => room.id === selected.id);
  const searching = normalizedSearch !== query || searchLoading;
  const rooms = snapshot?.rooms || [];
  function openRoom(room) { setSelected(room); setTab('audio'); }
  function moveTab(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 'audio' : event.key === 'End' ? 'logs' : tab === 'audio' ? 'logs' : 'audio';
    setTab(next); document.getElementById(`monitor-tab-${next}`)?.focus();
  }

  if (accessError) return <section><p role="alert" className="monitor-error">{accessError}</p><button onClick={() => setAccessError('')}>Retry access</button></section>;
  return <>
    <section className="monitor-section" aria-label="Active rooms">
      <div className="monitor-section-head"><div><h2>Active rooms {snapshot && <span className="monitor-count">{snapshot.totalRooms}</span>}</h2>
        <p className={`monitor-freshness ${error ? 'monitor-stale' : ''}`} role="status">{snapshot ? `${error ? 'Last successful update' : 'Updated'} ${displayDate(snapshot.generatedAtTs)} · refreshes every 3 seconds` : error ? 'Room list unavailable' : 'Loading active conversations…'}</p>
      </div><div className="monitor-controls"><label className="monitor-search">Find a room<input type="search" placeholder="Name, avaName, avaId or room ID" maxLength={128} value={search} onChange={(event) => setSearch(event.target.value)} /></label><button className="monitor-secondary" onClick={() => setRefresh((value) => value + 1)}>Refresh</button></div></div>
      {error && <p role="alert" className="monitor-error">{error}</p>}
      {searching && <p role="status">Searching active rooms…</p>}
      {snapshot?.truncated && <p className="monitor-note">Showing {snapshot.rooms.length} of {snapshot.matchingRooms ?? snapshot.totalRooms} {snapshot.query ? 'matching ' : ''}active rooms.</p>}
      <div className="monitor-scroll"><table className="monitor-room-table"><thead><tr><th scope="col">Started</th><th scope="col">Running</th><th scope="col">avaName</th><th scope="col">First and last name</th></tr></thead>
        <tbody aria-busy={searching}>{rooms.map((room) => <tr key={room.id} className={selected?.id === room.id ? 'monitor-selected' : ''} onClick={() => openRoom(room)}>
          <td><button className="monitor-room-open" aria-label={`Open conversation ${room.avaName || room.id} started ${displayDate(room.startedAtTs)}`} aria-pressed={selected?.id === room.id} onClick={(event) => { event.stopPropagation(); openRoom(room); }}>{displayDate(room.startedAtTs)}</button></td>
          <td className="monitor-duration" data-duration>{runningTime(room.startedAtTs, now)}</td><td>{room.avaName || '—'}</td><td>{fullName(room)}</td>
        </tr>)}</tbody></table></div>
      {snapshot && !searching && !error && !rooms.length && <p className="monitor-empty">{query ? 'No active conversations match your search.' : 'No active conversations right now.'}</p>}
    </section>
    {selected && <section className="monitor-room-detail" aria-label="Selected conversation" key={selected.id}>
      <div className="monitor-room-head"><div><div className="monitor-eyebrow">CONVERSATION</div><h2>{currentRoom.avaName || 'Room details'} <span className="monitor-room-person">{fullName(currentRoom)}</span></h2><p className="monitor-mono">{selected.id}</p></div><button className="monitor-secondary" onClick={() => setSelected(null)}>Close room</button></div>
      <div className="monitor-room-facts"><span>Started <strong>{displayDate(currentRoom.startedAtTs)}</strong></span><span>{ended ? 'Status' : 'Running'} <strong>{ended ? 'Ended' : runningTime(currentRoom.startedAtTs, now)}</strong></span></div>
      {ended && <p className="monitor-note">This conversation has ended. Its reported logs remain available.</p>}
      {!ended && <LiveKitRoomTranscript roomId={selected.id} user={user} onAccessError={onAccessError} />}
      <div className="monitor-tabs" role="tablist" aria-label="Conversation details" onKeyDown={moveTab}>
        {['audio', 'logs'].map((name) => <button key={name} id={`monitor-tab-${name}`} role="tab" aria-selected={tab === name} aria-controls={`monitor-panel-${name}`} tabIndex={tab === name ? 0 : -1} onClick={() => setTab(name)}>{name === 'audio' ? 'Audio' : 'Logs'}</button>)}
      </div>
      <div role="tabpanel" id="monitor-panel-audio" aria-labelledby="monitor-tab-audio" hidden={tab !== 'audio'}>{!ended && <LiveKitRoomAudio roomId={selected.id} user={user} onAccessError={onAccessError} />}</div>
      <div role="tabpanel" id="monitor-panel-logs" aria-labelledby="monitor-tab-logs" hidden={tab !== 'logs'}>{tab === 'logs' && <LiveKitRoomLogs roomId={selected.id} user={user} onAccessError={onAccessError} />}</div>
    </section>}
  </>;
}

export default function LiveKitMonitoring() {
  const [user, setUser] = useState(undefined);
  const [view, setView] = useState('rooms');
  const [signOutError, setSignOutError] = useState('');
  useEffect(() => authRef.onAuthStateChanged((nextUser) => {
    setUser(nextUser);
    setView('rooms');
    setSignOutError('');
  }), []);
  const project = authRef.app.options.projectId;
  function moveView(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 'rooms' : event.key === 'End' ? 'overview' : view === 'rooms' ? 'overview' : 'rooms';
    setView(next);
    document.getElementById(`monitor-view-tab-${next}`)?.focus();
  }
  return <main className="monitor-page">
    <header className="monitor-topbar">
      <div className="monitor-brand"><span className="monitor-logo">ava</span><span className="monitor-divider">/</span> LiveKit monitor</div>
      <div className="monitor-view-tabs" role="tablist" aria-label="Dashboard views" onKeyDown={moveView}>
        {['rooms', 'overview'].map((name) => <button key={name} id={`monitor-view-tab-${name}`} role="tab" aria-selected={view === name} aria-controls={`monitor-view-panel-${name}`} tabIndex={view === name ? 0 : -1} onClick={() => setView(name)}>{name === 'rooms' ? 'Rooms' : 'Overview'}</button>)}
      </div>
      {user && <button className="monitor-secondary monitor-sign-out" onClick={() => { void authRef.signOut().catch((error) => setSignOutError(error.message)); }}>Sign out</button>}
    </header>
    <div className="monitor-content"><div className="monitor-title"><div><div className="monitor-eyebrow">LIVEKIT MONITOR</div><h1>{view === 'rooms' ? 'Conversations, live.' : 'Conversation health'}</h1><p>{view === 'rooms' ? 'Open a room to follow its transcript, listen to audio and inspect logs.' : 'Connection outcomes and room activity from the current Firebase deployment.'}</p></div><span className="monitor-env">{project === 'ava-product' ? 'Production' : project === 'ava-app-dev-92f7f' ? 'Stage' : project}</span></div>
      {signOutError && <p role="alert" className="monitor-error">{signOutError}</p>}
      {user === undefined && <p role="status">Checking sign-in…</p>}
      {user === null && <SignIn />}
      <div className="monitor-view-panel" id="monitor-view-panel-rooms" role="tabpanel" aria-labelledby="monitor-view-tab-rooms" hidden={view !== 'rooms'}>
        {user && view === 'rooms' && <Rooms key={user.uid} user={user} />}
      </div>
      <div className="monitor-view-panel" id="monitor-view-panel-overview" role="tabpanel" aria-labelledby="monitor-view-tab-overview" hidden={view !== 'overview'}>
        {user && view === 'overview' && <LiveKitOverview key={user.uid} user={user} />}
      </div>
    </div>
  </main>;
}
